import {useEffect, useMemo} from "react";
import {RouterProvider} from "react-router-dom";

import {Chart} from "chart.js";

import {useCheckPermissionAdmin} from "../hooks/permissionHooks";
import {useRevaOrderTypeLock} from "../hooks/valueOrderEntitlementsHooks";
import {selectLoggedIn, selectPermissions} from "../redux/authSlice";
import {useRevaSelector} from "../redux/revaHooks";
import {selectStaticDataLoaded} from "../redux/specSelectors";
import {selectDarkTheme} from "../redux/themeSlice";
import {routerFactory} from "../router";
import {isManualPath} from "../util/revaManuals";
import Footer from "./Footer";
import LoginForm from "./LoginForm";
import PageManual from "./PageManual";
import ResetPasswordPage from "./ResetPasswordPage";
import {RevaOrderTypeNotEnabledPage} from "./RevaOrderTypeNotEnabled";
import StaticDataLoadingIndicator from "./StaticDataLoadingIndicator";
import VerifyEmailPage from "./VerifyEmailPage";

/**
 * #19351: paths of the password-reset and e-mail-verification pages sent by e-mail. Checked directly
 * against `window.location` (not a router match) because they must render regardless of login state — the
 * login-gated `RouterProvider` below is only built once a user is logged in, so a route declared inside
 * `router.tsx` would stay unreachable for the very users who need these pages (they forgot their password,
 * or need to prove a mailbox they don't yet have a session for — both by definition possibly logged out).
 * See `ResetPasswordPage`/`VerifyEmailPage` for the corresponding "no Router context" consequences.
 */
const RESET_PASSWORD_PATH = "/reset-password";
const VERIFY_EMAIL_PATH = "/verify-email";

export default function AppContainer() {
    const loggedIn = useRevaSelector(selectLoggedIn);
    const staticDataLoaded = useRevaSelector(selectStaticDataLoaded);
    const isDarkTheme = useRevaSelector(selectDarkTheme);
    const permissions = useRevaSelector(selectPermissions);
    const isAdmin = useCheckPermissionAdmin();
    // Fehlt dem Auftraggeber der Lizenz die Auftragsart REVA, sehen nur Admins die App (mit Warnhinweis in App).
    const revaOrderTypeLock = useRevaOrderTypeLock();
    const revaOrderTypeBlocked = revaOrderTypeLock.notEnabled && !isAdmin;

    // Den Router erst bauen, wenn Login UND statische Daten geladen sind – d.h. wenn die
    // Permissions des Users tatsächlich vorliegen. createBrowserRouter ruft intern sofort
    // .initialize() auf und führt damit die Loader der aktuellen URL bereits bei der Erzeugung aus
    // (react-router 7). Würde der Router früher – während des Auto-Logins mit noch leeren
    // Permissions – erzeugt, leiteten die rechtebehafteten Loader (/start/correction,
    // /start/teamadmin) einen direkt aufgerufenen/neu geladenen URL sofort auf /start/wip um, bevor
    // der Login aufgelöst ist. Das verschöbe /start/wip in die History, sodass man trotz Rechten dort
    // landet (#19113). Durch das Aufschieben nutzt der Loader-Snapshot dieselben Permissions wie die
    // sichtbaren Tabs.
    const router = useMemo(
        () => (loggedIn && staticDataLoaded ? routerFactory(permissions) : null),
        [loggedIn, staticDataLoaded, permissions],
    );

    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        Chart.defaults.font.family = documentStyle.getPropertyValue("--font-family");
    }, [isDarkTheme]);

    const isResetPasswordPage = window.location.pathname === RESET_PASSWORD_PATH;
    const isVerifyEmailPage = window.location.pathname === VERIFY_EMAIL_PATH;
    // #19302: Handbuch-Seite (QR-Code-Ziel, meist am Handy) direkt nach der Anmeldung, ohne Router, ohne statische
    // Daten und ohne Fußzeile. Sie verlässt sich auf eine harte Navigation („REVA starten“), daher genügt der Pfad.
    const isManualPage = isManualPath(window.location.pathname);

    return (
        <div id="App" className="App flex flex-col">
            {isResetPasswordPage ? (
                <ResetPasswordPage />
            ) : isVerifyEmailPage ? (
                <VerifyEmailPage />
            ) : loggedIn && isManualPage ? (
                <PageManual />
            ) : loggedIn ? (
                revaOrderTypeBlocked ? (
                    <RevaOrderTypeNotEnabledPage />
                ) : router && !revaOrderTypeLock.pending ? (
                    <RouterProvider router={router} />
                ) : (
                    <StaticDataLoadingIndicator />
                )
            ) : (
                <LoginForm />
            )}
            {!(loggedIn && isManualPage) && <Footer />}
        </div>
    );
}
